import axios from "axios";


import React, { useEffect, useState } from "react";

import {

  useLocation,

  useNavigate,

  useParams,

} from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import { fetchPriceSummary } from "../../actions/priceActions";

import {

  ArrowLeft,

  ArrowRight,

  Banknote,

  Check,

  CreditCard,

  Leaf,

  LockKeyhole,

  MessageSquareText,

  ShoppingBag,

  Utensils,

} from "lucide-react";

import { createOrder } from "../../actions/orderActions";

import { useCart } from "../../components/CartContext";

import { useRestaurantSession } from "../../components/RestaurantSessionProvider";

import {

  isSessionExpiredError,

  getSessionExpiredMessage,

} from "../../utils/sessionErrorHandler";

import "./css/OrderScreen.css";

const formatPrice = (value) =>

  Number(value || 0).toFixed(2);

function OrderScreen() {

  const navigate = useNavigate();

  const location = useLocation();

  const dispatch = useDispatch();

  const { restaurantId, tableId } = useParams();

  const { sessionToken } = useRestaurantSession();

  const { cart, clearCart } = useCart();

  // Recalculate prices with Django rather than trusting navigation state.
  const { loading: priceLoading, summary: priceSummary, error: priceError } =
    useSelector((state) => state.price || {});

  useEffect(() => {
    if (restaurantId && cart?.length) {
      dispatch(fetchPriceSummary(restaurantId, cart.map((item) => ({
        menu_item_id: item.id,
        quantity: item.quantity,
      }))));
    }
  }, [dispatch, restaurantId, cart]);

  const subtotal = Number(priceSummary?.subtotal ?? 0);
  const tax = Number(priceSummary?.tax ?? 0);
  const serviceCharge = Number(priceSummary?.service_charge ?? 0);
  const discount = Number(priceSummary?.discount ?? 0);
  const total = Number(priceSummary?.total ?? 0);

  const [paymentMethod, setPaymentMethod] = useState("");

  const [paymentOptions, setPaymentOptions] = useState(null);
  const onlineAllowed = paymentOptions?.allow_online_payment === true;
  const cashAllowed = paymentOptions?.allow_cash_payment === true;

  useEffect(() => {
    let active = true;
    setPaymentOptions(null);
    setPaymentMethod("");
    if (!restaurantId) return () => { active = false; };
    axios.get(`/api/restaurants/${encodeURIComponent(restaurantId)}/payment-options/`)
      .then(({ data }) => { if (active) setPaymentOptions(data); })
      .catch(() => {
        if (active) {
          setPaymentOptions({ allow_online_payment: false, allow_cash_payment: false });
          setError("Unable to load payment options. Please refresh.");
        }
      });
    return () => { active = false; };
  }, [restaurantId]);

  useEffect(() => {
    if (paymentOptions === null) return;
    setPaymentMethod((current) => {
      if (current === "CASH" && cashAllowed) return current;
      if (current === "ONLINE" && onlineAllowed) return current;
      return cashAllowed ? "CASH" : onlineAllowed ? "ONLINE" : "";
    });
  }, [paymentOptions, cashAllowed, onlineAllowed]);


  const [notes, setNotes] = useState("");

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  const homePath =

    `/${restaurantId}/table/${tableId}`;

  const hasPriceSummary = Boolean(
    cart?.length && !priceLoading && !priceError && priceSummary &&
    Number.isFinite(total) && total >= 0
  );

  const handleBack = () => {

    if (loading) return;

    navigate(`${homePath}/checkout`);

  };

  const selectPaymentMethod = (method) => {

    if (loading || !paymentOptions ||
        (method === "ONLINE" && !onlineAllowed) ||
        (method === "CASH" && !cashAllowed)) return;

    setPaymentMethod(method);

    setError("");

  };

  /* PLACE ORDER - SAME BACKEND FUNCTIONALITY */

  const handlePlaceOrder = async () => {

    if (loading) return;

    if (!paymentOptions || !paymentMethod ||
        (paymentMethod === "ONLINE" && !onlineAllowed) ||
        (paymentMethod === "CASH" && !cashAllowed)) {

      setError("Please select an available payment method.");

      return;

    }

    if (!cart || cart.length === 0) {

      setError("Your cart is empty.");

      return;

    }

    if (!hasPriceSummary) {

      setError(

        "Order total is unavailable. Please wait for backend pricing or return to checkout."

      );

      return;

    }

    if (!sessionToken) {

      setError(

        "Dining session is not ready. Please try again."

      );

      return;

    }

    try {

      setLoading(true);

      setError("");

      const orderData = {

        items: cart.map((item) => ({

          menu_item_id: item.id,

          quantity: item.quantity,

        })),

        session_token: sessionToken,

        payment_method: paymentMethod,

        table_id: Number(tableId),

        notes: notes.trim(),

      };

      const response = await dispatch(

        createOrder(restaurantId, orderData)

      );

      const orderId =

        response?.order_id || response?.id;

      const confirmedTotal = Number(response?.total ?? total);

      if (!orderId) {

        throw new Error(

          "Order response did not include an order ID. Please check your order status before trying again."

        );

      }

      /* CASH PAYMENT */

      if (paymentMethod === "CASH") {

        clearCart();

        navigate(`${homePath}/success`, {

          replace: true,

          state: {

            orderId,

            paymentMethod: "CASH",

            total: confirmedTotal,

          },

        });

        return;

      }

      /* ONLINE PAYMENT */

      if (paymentMethod === "ONLINE") {

        navigate(`${homePath}/payment`, {

          replace: true,

          state: {

            orderId,

            subtotal: Number(subtotal),

            tax: Number(tax),

            serviceCharge: Number(serviceCharge),

            total: confirmedTotal,

          },

        });

      }

    } catch (err) {

      console.error("Create order error:", err);

      if (isSessionExpiredError(err)) {

        setError(

          "Your table session has ended. Please scan the table QR code again to start a new session."

        );

        return;

      }

      setError(

        getSessionExpiredMessage(err) ||

          err?.message ||

          "Unable to place your order."

      );

    } finally {

      setLoading(false);

    }

  };

  return (

    <main className="theme10-order-page">

      {/* PAGE HEADER */}

      <section className="theme10-order-header">

        <div className="theme10-order-container">

          <button

            type="button"

            className="theme10-order-back"

            onClick={handleBack}

            disabled={loading}

          >

            <ArrowLeft size={17} />

            Back to Checkout

          </button>

          <div className="theme10-order-title-row">

            <div className="theme10-order-heading">

              <span className="theme10-order-label">

                THE FINAL STEP

              </span>

              <h1>

                Place Your <em>Order.</em>

              </h1>

              <p>

                Choose your payment method, add any special

                requests, and get ready for something delicious.

              </p>

            </div>

            <div className="theme10-order-table">

              <span>YOUR TABLE</span>

              <strong>{tableId}</strong>

            </div>

          </div>

        </div>

        <div className="theme10-order-header-shape" />

      </section>

      {/* MAIN CONTENT */}

      <section className="theme10-order-content">

        <div className="theme10-order-container">

          <div className="theme10-order-layout">

            {/* LEFT CONTENT */}

            <div className="theme10-order-main">

              {/* PAYMENT METHOD */}

              <section className="theme10-order-section">

                <div className="theme10-order-section-title">

                  <span className="theme10-order-step">

                    01

                  </span>

                  <div>

                    <small>PAYMENT METHOD</small>

                    <h2>How would you like to pay?</h2>

                  </div>

                </div>

                <div className="theme10-payment-options">

                  {/* CASH */}

                  <button

                    type="button"

                    className={`theme10-payment-option ${

                      paymentMethod === "CASH"

                        ? "selected"

                        : ""

                    }`}

                    onClick={() =>

                      selectPaymentMethod("CASH")

                    }

                    aria-pressed={paymentMethod === "CASH"}

                    disabled={loading || !cashAllowed}
                    aria-disabled={!cashAllowed}
                    style={{ opacity: cashAllowed ? 1 : 0.45, cursor: cashAllowed ? "pointer" : "not-allowed" }}

                  >

                    <div className="theme10-payment-icon">

                      <Banknote size={25} />

                    </div>

                    <div className="theme10-payment-info">

                      <strong>Cash Payment</strong>

                      <span>

                        Enjoy your meal and pay with cash

                        at your table.

                      </span>

                    </div>

                    <div className="theme10-payment-check">

                      {paymentMethod === "CASH" && (

                        <Check size={15} />

                      )}

                    </div>

                  </button>

                  {/* ONLINE */}

                  <button

                    type="button"

                    className={`theme10-payment-option ${

                      paymentMethod === "ONLINE"

                        ? "selected"

                        : ""

                    }`}

                    onClick={() =>

                      selectPaymentMethod("ONLINE")

                    }

                    aria-pressed={paymentMethod === "ONLINE"}

                    disabled={loading || !onlineAllowed}
                    aria-disabled={!onlineAllowed}
                    style={{ opacity: onlineAllowed ? 1 : 0.45, cursor: onlineAllowed ? "pointer" : "not-allowed" }}

                  >

                    <div className="theme10-payment-icon">

                      <CreditCard size={24} />

                    </div>

                    <div className="theme10-payment-info">

                      <strong>Online Payment</strong>

                      <span>

                        Pay securely online using Razorpay.

                      </span>

                    </div>

                    <div className="theme10-payment-check">

                      {paymentMethod === "ONLINE" && (

                        <Check size={15} />

                      )}

                    </div>

                  </button>

                </div>

                <div className="theme10-payment-hint">

                  <LockKeyhole size={15} />

                  <span>

                    Your payment choice will be applied

                    when your order is created.

                  </span>

                </div>

              </section>

              {/* SPECIAL INSTRUCTIONS */}

              <section className="theme10-order-section">

                <div className="theme10-order-section-title">

                  <span className="theme10-order-step">

                    02

                  </span>

                  <div>

                    <small>MAKE IT YOURS · OPTIONAL</small>

                    <h2>Special instructions</h2>

                  </div>

                </div>

                <div className="theme10-notes-box">

                  <div className="theme10-notes-icon">

                    <MessageSquareText size={19} />

                  </div>

                  <textarea

                    value={notes}

                    onChange={(event) =>

                      setNotes(event.target.value)

                    }

                    placeholder="Any special request for the kitchen? For example: less spicy, extra raita, no onions..."

                    rows={5}

                    maxLength={500}

                    disabled={loading}

                  />

                  <span className="theme10-notes-leaf">

                    <Leaf size={19} />

                  </span>

                </div>

                <div className="theme10-notes-bottom">

                  <span>

                    We'll pass your instructions

                    to the kitchen.

                  </span>

                  <span>{notes.length}/500</span>

                </div>

              </section>

              {/* TABLE DETAILS */}

              <div className="theme10-order-table-card">

                <div className="theme10-order-table-number">

                  <span>TABLE</span>

                  <strong>{tableId}</strong>

                </div>

                <div className="theme10-order-table-info">

                  <span>YOUR DINING SPOT</span>

                  <h3>Table {tableId}</h3>

                  <p>

                    Your order will be prepared and served

                    at this table.

                  </p>

                </div>

                <div className="theme10-order-table-status">

                  <Check size={18} />

                </div>

              </div>

              {/* FOOD NOTE */}

              <div className="theme10-order-fresh-note">

                <span>

                  <Utensils size={17} />

                </span>

                <p>

                  Made with care, served with flavour.

                  Your food will be prepared after your

                  order is confirmed.

                </p>

              </div>

              {/* ERROR */}

              {error && (

                <div

                  className="theme10-order-error"

                  role="alert"

                >

                  <strong>Unable to continue</strong>

                  <p>{error}</p>

                </div>

              )}

            </div>

            {/* RIGHT ORDER SUMMARY */}

            <aside className="theme10-order-summary">

              <div className="theme10-order-summary-icon">

                <ShoppingBag size={22} />

              </div>

              <span className="theme10-order-summary-label">

                03 · YOUR FINAL SUMMARY

              </span>

              <h2>

                Your <em>Order.</em>

              </h2>

              <p className="theme10-order-summary-subtitle">

                All your favourites, ready for the kitchen.

              </p>

              {/* CART ITEMS */}

              <div className="theme10-order-summary-items">

                {cart?.map((item) => (

                  <div

                    className="theme10-order-summary-item"

                    key={item.id}

                  >

                    <div>

                      <small>{item.quantity}×</small>

                      <span>{item.name}</span>

                    </div>

                    <strong>

                      ₹{formatPrice(

                        Number(item.price) *

                          Number(item.quantity)

                      )}

                    </strong>

                  </div>

                ))}

              </div>

              <div className="theme10-order-divider" />

              {/* PRICES */}

              <div className="theme10-order-price-row">

                <span>Subtotal</span>

                <strong>

                  ₹{formatPrice(subtotal)}

                </strong>

              </div>

              <div className="theme10-order-price-row">

                <span>Tax</span>

                <strong>₹{formatPrice(tax)}</strong>

              </div>

              <div className="theme10-order-price-row">

                <span>Service Charge</span>

                <strong>

                  ₹{formatPrice(serviceCharge)}

                </strong>

              </div>

              <div className="theme10-order-divider" />

              {/* TOTAL */}

              <div className="theme10-order-total">

                <div>

                  <span>TOTAL AMOUNT</span>

                  <small>

                    Including applicable charges

                  </small>

                </div>

                <strong>

                  ₹{formatPrice(total)}

                </strong>

              </div>

              {/* SELECTED PAYMENT */}

              <div

                className={`theme10-order-payment-status ${

                  paymentMethod ? "selected" : ""

                }`}

              >

                {paymentMethod ? (

                  <>

                    <Check size={16} />

                    <span>

                      {paymentMethod === "CASH"

                        ? "Cash payment selected"

                        : "Online payment selected"}

                    </span>

                  </>

                ) : (

                  <>

                    <CreditCard size={16} />

                    <span>

                      Select a payment method

                    </span>

                  </>

                )}

              </div>

              {/* PLACE ORDER */}

              <button

                type="button"

                className="theme10-place-order-button"

                onClick={handlePlaceOrder}

                disabled={

                  loading ||

                  !paymentOptions || !paymentMethod ||
                  (paymentMethod === "ONLINE" && !onlineAllowed) ||
                  (paymentMethod === "CASH" && !cashAllowed) ||

                  !cart?.length ||

                  !hasPriceSummary ||
                !sessionToken

                }

              >

                {loading ? (

                  <>

                    <span className="theme10-order-spinner" />

                    Creating Order...

                  </>

                ) : (

                  <>

                    {paymentMethod === "ONLINE"

                      ? "Continue to Payment"

                      : "Place Order"}

                    <ArrowRight size={19} />

                  </>

                )}

              </button>

              <button

                type="button"

                className="theme10-order-edit-button"

                onClick={handleBack}

                disabled={loading}

              >

                <ArrowLeft size={15} />

                Back to Checkout

              </button>

              <div className="theme10-order-secure-note">

                <LockKeyhole size={15} />

                <span>

                  Your order information is securely processed.

                </span>

              </div>

            </aside>

          </div>

        </div>

      </section>

    </main>

  );

}

export default OrderScreen;